<script lang="ts">
  import { onMount } from 'svelte'

  import { fetchConfig } from './lib/api/client'
  import { initLayers } from './lib/state/layers.svelte'
  import { initModels, startCatalogPolling } from './lib/state/models.svelte'
  import { watchLayout } from './lib/state/layout.svelte'
  import { startConnectionWatch } from './lib/state/connection.svelte'
  import { boot, startHereRecheck, watchNotificationOpens } from './lib/state/here.svelte'
  import { startForecastRefresh } from './lib/state/location.svelte'
  import { startRadarRefresh } from './lib/state/now.svelte'
  import { watchPlacesLinks } from './lib/state/places.svelte'
  import { loadPrefs } from './lib/state/prefs.svelte'
  import { setConfigSource, setPushConfig, startPushSync } from './lib/state/push.svelte'
  import { initTheme } from './lib/state/theme.svelte'
  import Shell from './lib/ui/Shell.svelte'

  // Preferences are read synchronously before the first render so the map
  // mounts with the right basemap and nothing flashes.
  loadPrefs()
  // The inline script in index.html already painted the right theme; this
  // takes it over, and the map reads it to pick the `auto` basemap.
  const stopTheme = initTheme()
  // Same reason, and it reads what loadPrefs() just put in `prefs`.
  initLayers()
  initModels()

  let cartoKey = $state('')

  // Boot happens once. These must not be $effects: restoring a location writes
  // the saved location back, which would re-trigger an effect that read it.
  onMount(() => {
    // Config is fetched in parallel with mounting. The map does not wait for
    // it; only the CARTO basemap needs the key, and MapView re-styles when one
    // arrives.
    void fetchConfig().then((config) => {
      cartoKey = config.carto_api_key
      // Push is offered only once the server says it has a key (plan §9.1).
      setPushConfig(config.push)
    })
    setConfigSource(async () => (await fetchConfig()).push)

    // A deep link, else My location, else the last place viewed (plan §5.2).
    void boot()

    // The model catalog is the one upstream that is a worker on this same box,
    // publishing a forecast step every few minutes. Nothing else watched it,
    // so a tab opened mid-ingest stayed on whatever was servable at boot.
    const stopLayout = watchLayout()
    const stopCatalog = startCatalogPolling()
    // The forecast was fetched once per selection and never again, so a phone
    // reopening the app showed the bundle from the last search.
    const stopForecast = startForecastRefresh()
    // The radar at the point, every two minutes: the one thing in the hero
    // that moves on the scale of a rain edge crossing it.
    const stopRadar = startRadarRefresh()
    // A phone picked up again may be somewhere else. Never prompts, and never
    // moves the camera.
    const stopHere = startHereRecheck()
    // Whether the server is answering, asked of the server: the browser's own
    // online flag is true behind a captive portal and while the pod restarts.
    const stopConnection = startConnectionWatch()
    // A `#places=` link opened while the app is up is a hash change, not a
    // boot. The boot reads one it was launched with.
    const stopPlacesLinks = watchPlacesLinks()
    // The server's copy of this device's alert places is rebuilt on every
    // open and every change (plan §9.4); nothing here ever prompts.
    const stopPush = startPushSync()
    // A notification tapped while the app is open is a message, not a boot.
    const stopNotificationOpens = watchNotificationOpens()
    return () => {
      stopTheme()
      stopNotificationOpens()
      stopPush()
      stopPlacesLinks()
      stopConnection()
      stopLayout()
      stopCatalog()
      stopForecast()
      stopRadar()
      stopHere()
    }
  })
</script>

<Shell {cartoKey} />
